import {
  Component,
  OnInit,
  OnDestroy,
  Injector,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
} from '@angular/core';
import { FormGroup } from '@angular/forms';
import { Subject, takeUntil } from 'rxjs';
import { FormlyFieldConfig, FormlyFormOptions } from '@ngx-formly/core';
import { FormBuilderService } from '../../../../core/services/form-builder.service';
import { FormBuilderManipulatorClass } from '../../../../core/utils/form-builder-manipulator-class.utils';
import { FormBuilderFieldConfigurationParserAndSetterClass } from '../../../../core/utils/form-fields-formly-configurations/form-builder-configuration-parser-and-setter.class';
import {
  IFormbuilderFieldTypeConfiguration,
  TConfigurationFormModelTypes,
} from '../../../../core/models/form-builder-types-configurations.model';
import { generateNewFieldKeyFromSettingsLabel } from '../../../../core/utils/form-fields-formly-configurations/form-fields-formly-settings/form-builder-default-settings-configuration-formly-fields.utils';
import {
  EIremboFormlyFieldSubTypes,
  EIremboFormlyFieldTypes,
  EIremboFormlyValidationTypes,
  ToastService,
} from '@irembo-andela/irembogov3-common';
import { BaseFormlyFieldConfiguration } from '../../../../core/utils/form-fields-formly-configurations/form-field-formly-types-main-configurations/form-field-formly-configuration-classes/base-formly-field-configuration';
import { getFormFieldConfigurationClass } from '../../../../core/utils/form-fields-formly-configurations/form-field-formly-types-main-configurations/form-field-formly-configuration-classes/utils/get-form-field-configuration-class';
import {
  clearFieldNew,
  isFieldNew,
  markFieldAsNew,
} from '../../utils/fb-new-field.util';
import { ServiceChangeRequestService } from '../../../../core/services/service-change-request.service';
import { preserveCustomRequiredExpression } from '../../../../core/utils/form-fields-formly-configurations/form-fields-formly-validations/form-fields-formly-validation-field-set-from-model.util';
import { IInternalDataset } from '../../../../core/models/internal-dataset.model';

interface IFbConfirmDialog {
  title: string;
  message: string;
  confirmLabel: string;
  cancelLabel: string;
  onConfirm: () => void;
  onCancel?: () => void;
}

enum EPropertyTab {
  Settings = 'Settings',
  Validation = 'Validation',
  Expressions = 'Expressions',
  Json = 'JSON',
}

@Component({
  selector: 'irembogov-fb-field-properties-panel',
  templateUrl: './fb-field-properties-panel.component.html',
  styleUrls: ['./fb-field-properties-panel.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FbFieldPropertiesPanelComponent implements OnInit, OnDestroy {
  configuredField: FormlyFieldConfig | null = null;
  fieldType: string | undefined;
  selectedTab: EPropertyTab = EPropertyTab.Settings;
  EPropertyTab = EPropertyTab;
  // Property search: filters the visible Settings/Validation rows by label.
  searchQuery = '';
  // Settings/Validation are edited then committed manually (Save). Structural
  // canvas ops (add/delete/reorder) keep auto-saving; once Saved here the
  // existing form autosave persists the change.
  isDirty = false;
  // In-panel confirmation modal (unsaved-changes guard + key-rename prompt).
  confirmDialog: IFbConfirmDialog | null = null;
  collapsedGroups: Record<string, boolean> = {
    advanced: true,
    api: true,
  };

  // Settings tab
  settingsForm = new FormGroup({});
  settingsModel: TConfigurationFormModelTypes = {};
  settingsFields: FormlyFieldConfig[] = [];
  // Search-filtered view of settingsFields actually bound to the form.
  displayedSettingsFields: FormlyFieldConfig[] = [];
  settingsOptions: FormlyFormOptions = {};

  // Validation tab
  validationForm = new FormGroup({});
  validationModel: Record<string, any> = {};
  validationFields: FormlyFieldConfig[] = [];
  // Search-filtered view of validationFields actually bound to the form.
  displayedValidationFields: FormlyFieldConfig[] = [];
  validationOptions: FormlyFormOptions = {};

  // JSON tab
  fieldJsonCode = '{}';
  jsonValidationError: string | null = null;
  jsonEditorOptions = {
    theme: 'vs',
    language: 'json',
    minimap: { enabled: false },
    lineNumbers: 'on' as const,
    folding: true,
    automaticLayout: true,
    scrollBeyondLastLine: false,
    wordWrap: 'on' as const,
  };

  tabs = [
    { key: EPropertyTab.Settings, label: 'Settings', icon: 'fa-solid fa-gear' },
    {
      key: EPropertyTab.Validation,
      label: 'Validation',
      icon: 'fa-solid fa-check-circle',
    },
    {
      key: EPropertyTab.Expressions,
      label: 'Expressions',
      icon: 'fa-solid fa-wand-magic-sparkles',
    },
    {
      key: EPropertyTab.Json,
      label: 'JSON',
      icon: 'fa-solid fa-code',
    },
  ];

  private destroy$ = new Subject<void>();
  // Set while we re-emit the current field to cancel a field switch, so the
  // action subscription ignores that one (self-induced) emission.
  private suppressActionGuard = false;
  private formBuilderService: FormBuilderService;
  private serviceChangeRequestService: ServiceChangeRequestService;
  private readonly toastService: ToastService;
  private formBuilderManipulator: FormBuilderManipulatorClass;
  private configParser: FormBuilderFieldConfigurationParserAndSetterClass;

  // Per-component configuration class (V1's rich settings/validation source).
  private fieldConfigClass?: BaseFormlyFieldConfiguration;
  private isNewConfigClass = false;

  // Field types that have a dedicated configuration class (mirrors V1). For
  // these, settings/validation come from the class — giving the full set of
  // options. Others fall back to the by-type config.
  private readonly fieldsWithNewConfigurations: EIremboFormlyFieldTypes[] = [
    EIremboFormlyFieldTypes.checkbox,
    EIremboFormlyFieldTypes.input,
    EIremboFormlyFieldTypes.textarea,
    EIremboFormlyFieldTypes.customdropdown,
    EIremboFormlyFieldTypes.customdropdownpaginated,
    EIremboFormlyFieldTypes.multicheckbox,
    EIremboFormlyFieldTypes.radio,
    EIremboFormlyFieldTypes.customidinput,
    EIremboFormlyFieldTypes.customphonenumber,
    EIremboFormlyFieldTypes.customdatepicker,
    EIremboFormlyFieldTypes.customfileupload,
    EIremboFormlyFieldTypes.customcascadingdropdowns,
    EIremboFormlyFieldTypes.customslotbooking,
    EIremboFormlyFieldTypes.customtininput,
    EIremboFormlyFieldTypes.customcrvsidinput,
    EIremboFormlyFieldTypes.customgenericdatafetch,
    EIremboFormlyFieldTypes.customapplicationnumberinput,
    EIremboFormlyFieldTypes.customsectionmessagewithlink,
    EIremboFormlyFieldTypes.customdoubleidinput,
    EIremboFormlyFieldTypes.customdatasettable,
    EIremboFormlyFieldTypes.customdatatable,
    EIremboFormlyFieldTypes.customdynamicrepeater,
    EIremboFormlyFieldTypes.customdatetimepicker,
    EIremboFormlyFieldTypes.customerrorcomponent,
    EIremboFormlyFieldTypes.customtimepicker,
    EIremboFormlyFieldTypes.customcurrencyformatinput,
    EIremboFormlyFieldTypes.customotpdatafetch,
    EIremboFormlyFieldTypes.customrepeater,
    EIremboFormlyFieldTypes.customtablewithinputform,
  ];

  constructor(private injector: Injector, private cdr: ChangeDetectorRef) {
    this.formBuilderService = this.injector.get(FormBuilderService);
    this.serviceChangeRequestService = this.injector.get(
      ServiceChangeRequestService
    );
    this.toastService = this.injector.get(ToastService);
    this.formBuilderManipulator = new FormBuilderManipulatorClass(
      this.injector
    );
    this.configParser = new FormBuilderFieldConfigurationParserAndSetterClass(
      this.injector
    );
  }

  ngOnInit(): void {
    this.formBuilderService.configureFieldAction$
      .pipe(takeUntil(this.destroy$))
      .subscribe(field => {
        // Ignore the emission we trigger ourselves to cancel a field switch.
        if (this.suppressActionGuard) {
          this.suppressActionGuard = false;
          return;
        }

        // Guard against losing unsaved settings/validation edits when moving
        // to another field (or closing): stay put and ask via the modal.
        const incomingKey = field?.key?.toString() ?? null;
        const currentKey = this.configuredField?.key?.toString() ?? null;
        if (
          this.isDirty &&
          this.configuredField &&
          incomingKey !== currentKey
        ) {
          const target = field;
          this.suppressActionGuard = true;
          this.formBuilderService.configureFieldAction$.next(
            this.configuredField
          );
          this.openConfirm({
            title: 'Unsaved changes',
            message:
              'You have unsaved changes to this field. Discard them and continue?',
            confirmLabel: 'Discard',
            cancelLabel: 'Keep editing',
            onConfirm: () => {
              this.isDirty = false;
              this.formBuilderService.configureFieldAction$.next(target);
            },
          });
          return;
        }

        this.isDirty = false;
        this.applySelectedField(field);
      });
  }

  private applySelectedField(field: FormlyFieldConfig | null): void {
    const currentKey = this.configuredField?.key?.toString() ?? null;
    const incomingKey = field?.key?.toString() ?? null;
    const fieldChanged = currentKey !== incomingKey;
    const fieldTypeChanged = this.fieldType !== field?.type?.toString();

    this.configuredField = field;
    this.fieldType = field?.type?.toString();
    // Start each newly selected field with an unfiltered property list.
    this.searchQuery = '';
    if ((fieldChanged || fieldTypeChanged) && field) {
      this.selectedTab = EPropertyTab.Settings;
      this.jsonValidationError = null;
    }
    if (field) {
      this.loadFieldConfiguration();
    }
    if (fieldTypeChanged && !fieldChanged) {
      this.cdr.detectChanges();
    } else {
      this.cdr.markForCheck();
    }
  }

  openConfirm(dialog: IFbConfirmDialog): void {
    this.confirmDialog = dialog;
    this.cdr.markForCheck();
  }

  onConfirmDialogConfirm(): void {
    const dialog = this.confirmDialog;
    this.confirmDialog = null;
    this.cdr.markForCheck();
    dialog?.onConfirm();
  }

  onConfirmDialogCancel(): void {
    const dialog = this.confirmDialog;
    this.confirmDialog = null;
    this.cdr.markForCheck();
    dialog?.onCancel?.();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get fieldLabel(): string {
    return (this.configuredField?.props?.['label'] as string) || 'Untitled';
  }

  onTabChange(tab: EPropertyTab): void {
    if (tab === EPropertyTab.Json) {
      this.syncFieldToJson();
    }
    this.selectedTab = tab;
    this.cdr.markForCheck();
  }

  onSettingsModelChange(): void {
    this.markDirty();
  }

  onValidationModelChange(): void {
    this.markDirty();
  }

  private markDirty(): void {
    this.isDirty = true;
    this.cdr.markForCheck();
  }

  // The expression editor mutates the field directly, so it applies live (the
  // dirty/Save flow is scoped to Settings + Validation). Just refresh/persist.
  onExpressionsChanged(): void {
    this.commitConfiguredField();
    this.formBuilderService.requestFormlyFieldsSave();
  }

  trackByTabKey(
    index: number,
    tab: { key: EPropertyTab; label: string; icon: string }
  ): string {
    return tab.key;
  }

  closePanel(): void {
    this.formBuilderService.configureFieldAction$.next(null);
  }

  onPaletteAddField(fieldConfig: FormlyFieldConfig): void {
    // Add field to the first block of the first section
    const fields = this.formBuilderService.formlyFieldsSubject$.value.fields;
    const sections = fields?.[0]?.fieldGroup;
    if (!sections?.length) return;

    // Find the first block with a fieldGroup
    for (const section of sections) {
      if (section.fieldGroup?.length) {
        const block = section.fieldGroup[0];
        if (block?.fieldGroup) {
          const field = generateNewFieldKeyFromSettingsLabel(fieldConfig);
          markFieldAsNew(field);
          block.fieldGroup.push(field);
          this.formBuilderService.refreshFormlyForm(false, true);
          this.formBuilderService.configureFieldAction$.next(field);
          return;
        }
      }
    }
  }

  toggleGroup(group: string): void {
    this.collapsedGroups[group] = !this.collapsedGroups[group];
    this.cdr.markForCheck();
  }

  // JSON tab methods
  onJsonCodeChange(code: string): void {
    this.fieldJsonCode = code;
    try {
      JSON.parse(code);
      this.jsonValidationError = null;
    } catch (e: any) {
      this.jsonValidationError = e.message;
    }
    this.cdr.markForCheck();
  }

  applyJsonChanges(): void {
    if (!this.configuredField || this.jsonValidationError) return;
    try {
      const parsed = JSON.parse(this.fieldJsonCode);
      if (parsed.expressions !== undefined) {
        this.configuredField.expressions = parsed.expressions;
      }
      if (parsed.props !== undefined) {
        Object.assign(this.configuredField.props || {}, parsed.props);
      }
      if (parsed.validators !== undefined) {
        this.configuredField.validators = parsed.validators;
      }
      if (parsed.validation !== undefined) {
        this.configuredField.validation = parsed.validation;
      }
      if (parsed.className !== undefined) {
        this.configuredField.className = parsed.className;
      }
      if (parsed.hide !== undefined) {
        this.configuredField.hide = parsed.hide;
      }
      if (parsed.defaultValue !== undefined) {
        this.configuredField.defaultValue = parsed.defaultValue;
      }
      this.commitConfiguredField();
      this.formBuilderService.requestFormlyFieldsSave();
      this.cdr.markForCheck();
    } catch {
      // invalid JSON while the user is editing → keep the last valid field config
    }
  }

  private syncFieldToJson(): void {
    if (!this.configuredField) return;
    const snapshot: Record<string, any> = {
      key: this.configuredField.key,
      type: this.configuredField.type,
      props: this.configuredField.props
        ? { ...this.configuredField.props }
        : {},
      expressions: this.configuredField.expressions || {},
      className: this.configuredField.className || '',
      hide: this.configuredField.hide,
      defaultValue: this.configuredField.defaultValue,
    };
    if (this.configuredField.validators) {
      snapshot['validators'] = this.configuredField.validators;
    }
    if (this.configuredField.validation) {
      snapshot['validation'] = this.configuredField.validation;
    }
    this.fieldJsonCode = JSON.stringify(snapshot, null, 2);
    this.jsonValidationError = null;
  }

  private loadFieldConfiguration(): void {
    if (!this.configuredField || !this.fieldType) return;

    const subtype = this.resolveSubtype(this.configuredField, this.fieldType);

    // Use V1's per-component configuration class when the type has one so the
    // Settings/Validation tabs show the full set of options (identical to V1);
    // otherwise fall back to the by-type config.
    this.fieldConfigClass = getFormFieldConfigurationClass(
      this.injector,
      this.configuredField
    );
    this.isNewConfigClass = this.fieldsWithNewConfigurations.includes(
      this.configuredField.type as EIremboFormlyFieldTypes
    );

    let config: IFormbuilderFieldTypeConfiguration;
    try {
      config = this.isNewConfigClass
        ? this.fieldConfigClass.getFormlyFieldConfigurationConfig()
        : this.configParser.getFormlyFieldConfigurationConfigByType(
            this.fieldType,
            subtype
          );
    } catch (e) {
      this.settingsFields = [];
      this.settingsModel = {};
      this.settingsForm = new FormGroup({});
      this.settingsOptions = { formState: { hideBuilderWrapper: true } };
      this.validationFields = [];
      console.error('Failed to load field configuration:', e);
      this.cdr.markForCheck();
      return;
    }

    // Settings
    if (config.settings) {
      this.settingsFields = config.settings.formlyForm || [];
      this.prepareFieldKeySettings(
        this.settingsFields,
        isFieldNew(this.configuredField)
      );
      this.ensureRequiredSetting(this.settingsFields, config.validations);
      this.settingsModel = this.isNewConfigClass
        ? this.fieldConfigClass.parseFieldConfigIntoSettingsFormModel()
        : this.configParser.parseFieldIntoSettingsFormModelByType(
            this.configuredField,
            this.fieldType
          );
      this.restoreSavedDatasetOption();
      (this.settingsModel as Record<string, unknown>)['required'] =
        this.isFieldRequired(this.configuredField);
      this.settingsForm = new FormGroup({});
      // hideBuilderWrapper renders property editors in end-user mode (single
      // label, compact controls). Matches V1's property panel.
      this.settingsOptions = { formState: { hideBuilderWrapper: true } };
    } else {
      this.settingsFields = [];
    }
    this.displayedSettingsFields = this.filterFieldsBySearch(
      this.settingsFields
    );

    // Validation
    if (config.validations) {
      this.validationFields = config.validations.formlyForm || [];
      // Hidden ref-path fields needed by conditional (cross-field) validations
      // to resolve the configured field's paths — exactly as V1 does.
      this.validationFields.push(
        ...this.generateConfiguredFieldModelAndRefPathsFormlyFields()
      );
      this.validationModel = this.isNewConfigClass
        ? this.fieldConfigClass.parseFieldConfigIntoValidationsFormModel()
        : this.configParser.parseFieldIntoValidationFormModelByType(
            this.configuredField,
            this.fieldType
          );
      this.validationForm = new FormGroup({});
      this.validationOptions = { formState: { hideBuilderWrapper: true } };
    } else {
      this.validationFields = [];
    }
    this.displayedValidationFields = this.filterFieldsBySearch(
      this.validationFields
    );

    this.cdr.markForCheck();
  }

  private restoreSavedDatasetOption(): void {
    if (
      this.configuredField?.type !==
      EIremboFormlyFieldTypes.customdropdownpaginated
    ) {
      return;
    }

    const datasetSection = (this.settingsModel as Record<string, any>)?.[
      'datasetSection'
    ];
    const savedDataset = datasetSection?.code;
    if (!savedDataset || typeof savedDataset !== 'object') return;

    this.setSavedDatasetOption(savedDataset);
    if (!savedDataset.__legacySelection || !savedDataset.id) return;

    const configuredFieldKey = this.configuredField.key?.toString();
    this.serviceChangeRequestService
      .getInternalDataset(savedDataset.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: response => {
          const currentDataset = datasetSection.code;
          if (
            this.configuredField?.key?.toString() !== configuredFieldKey ||
            !response.data ||
            currentDataset?.id !== savedDataset.id ||
            !currentDataset?.__legacySelection
          ) {
            return;
          }
          datasetSection.code = response.data;
          this.setSavedDatasetOption(response.data);
          this.settingsForm
            .get(['datasetSection', 'code'])
            ?.setValue(response.data, { emitEvent: false });
          this.cdr.markForCheck();
        },
      });
  }

  private setSavedDatasetOption(dataset: IInternalDataset): void {
    const datasetSettings = this.settingsFields.find(
      field => field.key === 'datasetSection'
    );
    const datasetSelector = datasetSettings?.fieldGroup?.find(
      field => field.key === 'code'
    );
    if (!datasetSelector?.props) return;

    datasetSelector.props.options = [dataset];
    this.settingsOptions.fieldChanges?.next({
      field: datasetSelector,
      type: 'expressionChanges',
      property: 'props.options',
      value: datasetSelector.props.options,
    });
  }

  onSearchChange(): void {
    this.displayedSettingsFields = this.filterFieldsBySearch(
      this.settingsFields
    );
    this.displayedValidationFields = this.filterFieldsBySearch(
      this.validationFields
    );
    this.cdr.markForCheck();
  }

  // Filters property rows by the search query (matched against the field
  // label). Hidden/structural fields (no label, hideField, or hide) are always
  // kept so functionality — e.g. the conditional-validation ref paths — is
  // never dropped while searching.
  private filterFieldsBySearch(
    fields: FormlyFieldConfig[]
  ): FormlyFieldConfig[] {
    const query = this.searchQuery.trim().toLowerCase();
    if (!query) return fields;
    return fields.filter(field => {
      const props = (field.props as Record<string, unknown>) || {};
      const label = (props['label'] as string | undefined)?.toLowerCase() ?? '';
      const isStructural = !label || !!props['hideField'] || !!field.hide;
      return isStructural || label.includes(query);
    });
  }

  // Ported verbatim from V1: two hidden, read-only fields carrying the
  // configured field's root-model and ref paths. Conditional validations use
  // these to build cross-field expressions. Typed config classes also set them
  // in the model directly; this additionally covers the by-type fallback path.
  private generateConfiguredFieldModelAndRefPathsFormlyFields(): FormlyFieldConfig[] {
    const configuredFieldLabelAndDetails =
      this.formBuilderService.getCurrentConfiguredFieldLabelAndDetails();
    return [
      {
        key: 'configuredFieldRootModelPath',
        type: EIremboFormlyFieldTypes.input,
        defaultValue: configuredFieldLabelAndDetails?.rootModelPath,
        props: {
          readonly: true,
          hideField: true,
        },
      },
      {
        key: 'configuredFieldRefPath',
        type: EIremboFormlyFieldTypes.input,
        defaultValue: configuredFieldLabelAndDetails?.fieldRefPath,
        props: {
          readonly: true,
          hideField: true,
        },
      },
    ];
  }

  // Tune the Field Key settings field:
  //  - accept any casing (drop V1's camelCase "validKey" rule; keep duplicate
  //    check) — the user's choice;
  //  - for EXISTING fields, disable the label→key sync hook so editing a label
  //    never silently changes an established key. New (just-added) fields keep
  //    the sync as a convenience until first save.
  // Done in V2 only, in-place on the freshly-built settings, so V1 is untouched.
  private prepareFieldKeySettings(
    fields: FormlyFieldConfig[],
    isNewField: boolean
  ): void {
    for (const field of fields) {
      if (field.key === 'fieldKey') {
        if (field.validators) {
          delete (field.validators as Record<string, unknown>)['validKey'];
        }
        if (field.validation?.messages) {
          delete (field.validation.messages as Record<string, unknown>)[
            'validKey'
          ];
        }
        if (!isNewField) {
          // Remove onInit (which wires the label→key watcher). The key is
          // already set on existing fields, so nothing else is lost.
          field.hooks = {};
        }
      }
      if (field.fieldGroup?.length) {
        this.prepareFieldKeySettings(field.fieldGroup, isNewField);
      }
    }
  }

  private ensureRequiredSetting(
    fields: FormlyFieldConfig[],
    validations?: IFormbuilderFieldTypeConfiguration['validations']
  ): void {
    const hasRequiredValidation = this.hasFieldWithKey(
      validations?.formlyForm || [],
      'required'
    );
    if (!hasRequiredValidation || this.hasFieldWithKey(fields, 'required')) {
      return;
    }

    const requiredField: FormlyFieldConfig = {
      key: 'required',
      type: EIremboFormlyFieldTypes.checkbox,
      props: {
        label: 'Required',
        hideHeader: true,
        placeholder: 'Required',
        required: false,
        formCheck: 'switch',
      },
      id: 'required',
    };

    const readonlyIndex = fields.findIndex(field => field.key === 'readonly');
    fields.splice(
      readonlyIndex >= 0 ? readonlyIndex : fields.length,
      0,
      requiredField
    );
  }

  private hasFieldWithKey(fields: FormlyFieldConfig[], key: string): boolean {
    return fields.some(field => {
      if (field.key === key) return true;
      return field.fieldGroup?.length
        ? this.hasFieldWithKey(field.fieldGroup, key)
        : false;
    });
  }

  private isFieldRequired(field: FormlyFieldConfig): boolean {
    return Boolean(field.props?.['defaultRequired'] ?? field.props?.required);
  }

  private syncRequiredSettingToValidationModel(): void {
    const settingsModel = this.settingsModel as Record<string, unknown>;
    if (!Object.prototype.hasOwnProperty.call(settingsModel, 'required')) {
      return;
    }
    const required = Boolean(settingsModel['required']);
    this.validationModel = {
      ...this.validationModel,
      required,
      validations: {
        ...(this.validationModel?.['validations'] || {}),
        [EIremboFormlyValidationTypes.required]: {
          ...(this.validationModel?.['validations']?.[
            EIremboFormlyValidationTypes.required
          ] || {}),
          logics: [
            {
              ...(this.validationModel?.['validations']?.[
                EIremboFormlyValidationTypes.required
              ]?.logics?.[0] || {}),
              [EIremboFormlyValidationTypes.required]: required,
            },
          ],
        },
      },
    };
  }

  // Mirrors V1: resolve the field subtype used by the by-type config fallback.
  private resolveSubtype(
    field: FormlyFieldConfig,
    fieldType: string
  ): EIremboFormlyFieldSubTypes | undefined {
    const props = (field.props as Record<string, unknown>) || {};
    if (fieldType === EIremboFormlyFieldTypes.customidinput) {
      const idType = props['idType'] as string | undefined;
      if (idType === 'NIN')
        return EIremboFormlyFieldSubTypes.CUSTOM_ID_INPUT_NIN;
      if (idType === 'NID')
        return EIremboFormlyFieldSubTypes.CUSTOM_ID_INPUT_NID;
      if (idType === 'CHILD_ID')
        return EIremboFormlyFieldSubTypes.CUSTOM_ID_INPUT_CHILD_ID;
      const url = (props['url'] as string | undefined)?.toLowerCase();
      if (url?.includes('identity/v1/nin-info'))
        return EIremboFormlyFieldSubTypes.CUSTOM_ID_INPUT_NIN;
      if (url?.includes('identity/v1/nid-info'))
        return EIremboFormlyFieldSubTypes.CUSTOM_ID_INPUT_NID;
      if (url?.includes('identity/v1/child-id-info'))
        return EIremboFormlyFieldSubTypes.CUSTOM_ID_INPUT_CHILD_ID;
    }
    const subType = props['subType'] as EIremboFormlyFieldSubTypes | undefined;
    if (
      fieldType === EIremboFormlyFieldTypes.customdropdownpaginated &&
      (subType ===
        EIremboFormlyFieldSubTypes.CUSTOM_DROPDOWN_DATAFETCH_PAGINATED ||
        subType ===
          EIremboFormlyFieldSubTypes.CUSTOM_DROPDOWN_DATASET_PAGINATED)
    ) {
      return subType;
    }
    if (
      fieldType === EIremboFormlyFieldTypes.customdropdown &&
      (subType === EIremboFormlyFieldSubTypes.CUSTOM_DROPDOWN_DATAFETCH ||
        subType === EIremboFormlyFieldSubTypes.CUSTOM_DROPDOWN_DATASET)
    ) {
      return subType;
    }
    return undefined;
  }

  // Commit the panel's Settings + Validation edits onto the field. Manual, so
  // the designer can configure freely and validate as a unit; once saved, the
  // existing form autosave persists it.
  save(): void {
    if (!this.configuredField || !this.fieldType) return;

    // Reject a duplicate label before applying anything — the backend enforces
    // unique labels form-wide, so don't save an edit that would fail. The panel
    // stays dirty so the user can correct the label.
    const proposedLabel = (this.settingsModel as Record<string, unknown>)?.[
      'label'
    ];
    if (
      typeof proposedLabel === 'string' &&
      proposedLabel.trim() &&
      this.formBuilderService.isDuplicateFieldLabel(
        proposedLabel.trim(),
        this.configuredField.key?.toString()
      )
    ) {
      this.toastService.show({
        body: `Another field already uses the label "${proposedLabel.trim()}". Labels must be unique — the change was not saved.`,
        type: 'warning',
      });
      return;
    }

    // Cascading dropdowns expand into multiple location fields on save (V1).
    if (
      this.configuredField.type ===
      EIremboFormlyFieldTypes.customcascadingdropdowns
    ) {
      this.syncRequiredSettingToValidationModel();
      const updatedFields =
        this.configParser.generateWidgetFieldsFromSettingsFormModel(
          this.settingsModel,
          this.configuredField,
          this.fieldType
        );
      const locationFieldIndex = updatedFields.findIndex(
        field => field.type === EIremboFormlyFieldTypes.customcascadingdropdowns
      );
      const countryRootIndex = updatedFields.findIndex(
        field => field.props?.['locationWidgetRoot']
      );
      const validationTargetIndex =
        locationFieldIndex >= 0 ? locationFieldIndex : countryRootIndex;
      if (validationTargetIndex >= 0) {
        updatedFields[validationTargetIndex] =
          this.configParser.updateFieldFromValidationsFormModel(
            this.validationModel,
            updatedFields[validationTargetIndex],
            this.fieldType
          );
      }
      if (locationFieldIndex >= 0 && countryRootIndex >= 0) {
        const countryRequired =
          updatedFields[locationFieldIndex].props?.['countryRequired'];
        const countryRoot = updatedFields[countryRootIndex];
        if (countryRoot.props) {
          countryRoot.props['countryRequired'] = countryRequired;
        }
      }
      const configuredLocationField = this.configuredField;
      // Replacing one Location widget with its generated fields clears the
      // current selection. The edits are committed at this point, so prevent
      // that expected selection change from opening the unsaved-changes guard.
      this.isDirty = false;
      this.formBuilderManipulator.replaceConfiguredFieldItemWithFields(
        updatedFields
      );
      clearFieldNew(configuredLocationField);
      this.refreshForm();
      this.formBuilderService.requestFormlyFieldsSave();
      this.cdr.markForCheck();
      return;
    }

    const oldKey = this.configuredField.key?.toString();
    const wasNew = isFieldNew(this.configuredField);

    // Settings then validation go through the by-type/config-class setters (V1).
    this.configuredField = this.configParser.updateFieldFromSettingsFormModel(
      this.settingsModel,
      this.configuredField,
      this.fieldType
    );
    this.syncRequiredSettingToValidationModel();
    // The validation setters always rewrite `props.required` to the default
    // checkbox-driven expression. If the designer authored a conditional
    // "required when …" rule in the Expressions tab, preserve it so it survives
    // this save and is still recognized when the field is reopened (ROSC-3499).
    this.configuredField = preserveCustomRequiredExpression(
      this.configuredField,
      field =>
        this.isNewConfigClass && this.fieldConfigClass
          ? this.fieldConfigClass.updateFieldConfigFromValidationsFormModel(
              this.validationModel
            )
          : this.configParser.updateFieldFromValidationsFormModel(
              this.validationModel,
              field,
              this.fieldType
            )
    );

    const newKey = this.configuredField.key?.toString();
    // Field is now committed: label edits no longer drive its key.
    clearFieldNew(this.configuredField);

    this.commitConfiguredField();
    this.formBuilderService.requestFormlyFieldsSave();
    this.isDirty = false;
    // A just-saved new field has become "existing": rebuild its settings so the
    // label→key sync hook is removed for subsequent edits.
    if (wasNew) {
      this.loadFieldConfiguration();
    }
    this.cdr.markForCheck();

    if (oldKey && newKey && oldKey !== newKey) {
      this.promptKeyRename(oldKey, newKey);
    }
  }

  // Drop unsaved edits: edits live only in the panel form until Save, so
  // reloading the field's current config resets the panel.
  discard(): void {
    if (this.configuredField) {
      this.loadFieldConfiguration();
    }
    this.isDirty = false;
    this.cdr.markForCheck();
  }

  // When the field key changes, references to the old key in other fields'
  // expressions/conditional-validations break. Offer to update them via the
  // modal.
  private promptKeyRename(oldKey: string, newKey: string): void {
    const refs = this.findKeyReferences(oldKey);
    if (!refs.length) return;

    const preview = refs.slice(0, 5).join(', ') + (refs.length > 5 ? '…' : '');
    this.openConfirm({
      title: 'Update key references?',
      message:
        `The key "${oldKey}" is referenced in ${refs.length} ` +
        `expression(s)/validation(s): ${preview}. Update them to "${newKey}"?`,
      confirmLabel: 'Update automatically',
      cancelLabel: 'Leave (review in JSON)',
      onConfirm: () => {
        this.replaceKeyReferences(oldKey, newKey);
        this.refreshForm();
      },
    });
  }

  private findKeyReferences(oldKey: string): string[] {
    const token = new RegExp(`\\b${this.escapeRegExp(oldKey)}\\b`);
    const hits: string[] = [];
    this.walkFields(this.getRootFields(), field => {
      const exprs = (field.expressions as Record<string, unknown>) || {};
      const referenced = Object.values(exprs).some(
        v => typeof v === 'string' && token.test(v)
      );
      if (referenced) {
        hits.push(
          (field.props?.['label'] as string) || field.key?.toString() || 'field'
        );
      }
    });
    return hits;
  }

  private replaceKeyReferences(oldKey: string, newKey: string): void {
    const token = new RegExp(`\\b${this.escapeRegExp(oldKey)}\\b`, 'g');
    this.walkFields(this.getRootFields(), field => {
      const exprs = field.expressions as Record<string, unknown> | undefined;
      if (!exprs) return;
      for (const key of Object.keys(exprs)) {
        const value = exprs[key];
        if (typeof value === 'string') {
          exprs[key] = value.replace(token, newKey);
        }
      }
    });
  }

  private getRootFields(): FormlyFieldConfig[] {
    return this.formBuilderService.formlyFieldsSubject$.value.fields || [];
  }

  private walkFields(
    fields: FormlyFieldConfig[] | undefined,
    fn: (field: FormlyFieldConfig) => void
  ): void {
    if (!fields) return;
    for (const field of fields) {
      fn(field);
      if (field.fieldGroup) {
        this.walkFields(field.fieldGroup, fn);
      }
      const fieldArray = field.fieldArray;
      if (fieldArray && typeof fieldArray !== 'function') {
        this.walkFields((fieldArray as FormlyFieldConfig).fieldGroup, fn);
      }
    }
  }

  private escapeRegExp(value: string): string {
    return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }

  private refreshForm(): void {
    this.formBuilderService.refreshFormlyForm(false, true);
  }

  private commitConfiguredField(): void {
    if (!this.configuredField) return;

    const parentType = this.configuredField.parent?.type?.toString();
    const fieldType = this.configuredField.type?.toString();

    if (parentType === EIremboFormlyFieldTypes.sections) {
      this.formBuilderManipulator.updateConfiguredSectionField(
        this.configuredField,
        true
      );
      return;
    }

    if (fieldType === EIremboFormlyFieldTypes.block) {
      this.formBuilderManipulator.updateConfiguredBlockField(
        this.configuredField,
        true
      );
      return;
    }

    this.formBuilderManipulator.updateConfiguredFieldItem(
      this.configuredField,
      true
    );
  }
}
